import { type ReactNode, useMemo } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { DynamicTextPropType } from '../../text/DynamicText';
import { Icon } from '../../assets/Icon/Icon';
import { sizing } from '../../common/sizing';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { styled } from '../../stitches.config';
import { DynamicText } from '../../text/DynamicText';

const EmptyStateIcon = styled(Icon, {
  variants: {
    size: {
      small: {
        size: '$16',
      },
      medium: {
        size: '$24',
      },
      large: {
        size: '$32',
      },
    },
  },
});
EmptyStateIcon.displayName = 'EmptyStateIcon';

const EmptyStateHeading = styled(DynamicText, {
  textAlign: 'center',
});
EmptyStateHeading.displayName = 'EmptyStateHeading';

const EmptyStateSubheading = styled(DynamicText, {
  textAlign: 'center',
});
EmptyStateSubheading.displayName = 'EmptyStateSubheading';

const EmptyStateHeader = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});
EmptyStateHeader.displayName = 'EmptyStateHeader';

const EmptyStateContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: '$560',

  variants: {
    size: {
      small: {
        gap: '$8',
      },
      medium: {
        gap: '$12',
      },
      large: {
        gap: '$16',
      },
    },
  },
});
EmptyStateContent.displayName = 'EmptyStateContent';

const EmptyStateContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',

  variants: {
    size: {
      small: {
        padding: `$12 ${sizing.sides}`,
      },
      medium: {
        minHeight: '$180',
        padding: `$20 ${sizing.sides}`,
      },
      large: {
        minHeight: '$180',
        padding: `$24 ${sizing.sides}`,
      },
    },
  },
});
EmptyStateContainer.displayName = 'EmptyStateContainer';

export interface EmptyStateProps {
  heading: ReactNode;
  subheading?: ReactNode;
  icon?: IconName;
  action?: ReactNode;
  size?: 'small' | 'medium' | 'large';
}

export function EmptyState({
  heading,
  subheading,
  icon,
  action,
  size = 'medium',
  ...remaining
}: EmptyStateProps) {
  const headingType = useMemo((): DynamicTextPropType => {
    switch (size) {
      case 'small':
        return 'small';
      case 'medium':
        return 'body';
      case 'large':
        return 'large';
      default:
        return 'body';
    }
  }, [size]);

  const subHeadingType = useMemo((): DynamicTextPropType => {
    switch (size) {
      case 'small':
        return 'small';
      case 'medium':
        return 'small';
      case 'large':
        return 'body';
      default:
        return 'small';
    }
  }, [size]);

  return (
    <EmptyStateContainer {...remaining} size={size}>
      <EmptyStateContent size={size}>
        {icon && <EmptyStateIcon size={size} icon={icon} variant="neutral" />}
        <EmptyStateHeader>
          <EmptyStateHeading context="heading" variant="neutral" weight="bold" type={headingType}>
            {heading}
          </EmptyStateHeading>
          {subheading && (
            <EmptyStateSubheading context="body" variant="neutral" type={subHeadingType}>
              {subheading}
            </EmptyStateSubheading>
          )}
        </EmptyStateHeader>
        {action && (
          <ControlGroup appearance="vivid" relation="separate" size="small" variant="secondary">
            {action}
          </ControlGroup>
        )}
      </EmptyStateContent>
    </EmptyStateContainer>
  );
}
